import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, Leaf, Lock, Eye,
  FileText, Trash2, Loader2,
  Upload, Award, TrendingDown, Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { getErrorMessage } from '@/utils/errorHelpers';
import { usePermissions, FEATURES } from '@/permissions/usePermissions';

const CREDIT_PROVIDERS = [
  { value: 'accu', label: 'ACCU (Australian Carbon Credit Units)' },
  { value: 'verra', label: 'Verra (VCS)' },
  { value: 'gold_standard', label: 'Gold Standard' },
  { value: 'other', label: 'Other' }
];

const OffsetsPage = () => {
  const navigate = useNavigate();
  const { can, canWithReason } = usePermissions();
  const [offsets, setOffsets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentOrg: organisation } = useOutletContext();

  // Form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showEditAllocationModal, setShowEditAllocationModal] = useState(false);
  const [showDeleteAllocationConfirm, setShowDeleteAllocationConfirm] = useState(null);
  const [selectedOffset, setSelectedOffset] = useState(null);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteOffsetConfirm, setShowDeleteOffsetConfirm] = useState(null);
  const [showDeleteCertificateConfirm, setShowDeleteCertificateConfirm] = useState(null);

  // Add form
  const [newOffset, setNewOffset] = useState({
    project_name: '',
    credit_provider: '',
    retirement_serial_number: '',
    vintage_year: new Date().getFullYear(),
    amount_tco2e: '',
    purchase_date: '',
    purchase_cost: '',
    notes: ''
  });

  // Allocate form
  const [allocation, setAllocation] = useState({
    financial_year: new Date().getFullYear() + 1,
    amount_tco2e: '',
    notes: ''
  });

  // Edit allocation form
  const [editAllocation, setEditAllocation] = useState({
    financial_year: '',
    amount_tco2e: '',
    notes: ''
  });

  // File upload
  const [uploadingCert, setUploadingCert] = useState(null);

  useEffect(() => {
    if (organisation?.id) {
      setLoading(true);
      fetchOffsetsData(organisation.id).finally(() => setLoading(false));
    }
  }, [organisation]);

  const fetchOffsetsData = async (orgId) => {
    try {
      // Fetch offsets with allocations
      const { data: offsetsData, error: offsetsError } = await supabase
        .from('carbon_offsets')
        .select(`
          *,
          offset_allocations (*)
        `)
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (offsetsError) throw offsetsError;

      // Calculate remaining for each offset
      const offsetsWithRemaining = (offsetsData || []).map(offset => {
        const allocated = (offset.offset_allocations || []).reduce(
          (sum, alloc) => sum + (alloc.amount_tco2e || 0), 0
        );
        return {
          ...offset,
          allocations: offset.offset_allocations || [],
          remaining_tco2e: offset.amount_tco2e - allocated
        };
      });

      setOffsets(offsetsWithRemaining);

      // Calculate summary
      await calculateSummary(orgId, offsetsWithRemaining);
    } catch (error) {
      console.error('Error fetching offsets:', error);
    }
  };

  const calculateSummary = async (orgId, offsetsData) => {
    try {
      const totalPurchased = offsetsData.reduce((sum, o) => sum + o.amount_tco2e, 0);
      const totalRetired = offsetsData.reduce((sum, o) =>
        sum + (o.allocations || []).reduce((s, a) => s + a.amount_tco2e, 0), 0
      );
      const remainingInventory = totalPurchased - totalRetired;

      // Get emissions by year for net emissions calculation
      const { data: emissionsData, error: emissionsError } = await supabase
        .from('emissions_data')
        .select('financial_year, co2_equivalent')
        .eq('organisation_id', orgId);

      if (emissionsError) throw emissionsError;

      // Group emissions by year
      const emissionsByYear = (emissionsData || []).reduce((acc, e) => {
        const fy = e.financial_year || new Date().getFullYear();
        acc[fy] = (acc[fy] || 0) + (e.co2_equivalent || 0);
        return acc;
      }, {});

      // Group allocations by year
      const allocationsByYear = {};
      offsetsData.forEach(offset => {
        (offset.allocations || []).forEach(alloc => {
          const fy = alloc.financial_year;
          allocationsByYear[fy] = (allocationsByYear[fy] || 0) + alloc.amount_tco2e;
        });
      });

      // Build by_year summary
      const allYears = new Set([...Object.keys(emissionsByYear), ...Object.keys(allocationsByYear)]);
      const byYear = {};
      allYears.forEach(year => {
        const grossEmissions = emissionsByYear[year] || 0;
        const retiredOffsets = allocationsByYear[year] || 0;
        byYear[year] = {
          gross_emissions: grossEmissions,
          retired_offsets: retiredOffsets,
          net_emissions: grossEmissions - retiredOffsets
        };
      });

      setSummary({
        total_purchased: totalPurchased,
        total_retired: totalRetired,
        remaining_inventory: remainingInventory,
        offsets_count: offsetsData.length,
        by_year: byYear
      });
    } catch (error) {
      console.error('Error calculating summary:', error);
    }
  };

  const handleAddOffset = async (e) => {
    e.preventDefault();
    if (!organisation) return;

    setSubmitting(true);

    try {
      // Convert date to ISO datetime format if provided
      let purchaseDate = null;
      if (newOffset.purchase_date) {
        const now = new Date();
        const [year, month, day] = newOffset.purchase_date.split('-').map(Number);
        const purchaseDateObj = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
        purchaseDate = purchaseDateObj.toISOString();
      }

      const { data, error } = await supabase
        .from('carbon_offsets')
        .insert({
          id: crypto.randomUUID(),
          organisation_id: organisation.id,
          project_name: newOffset.project_name,
          credit_provider: newOffset.credit_provider,
          retirement_serial_number: newOffset.retirement_serial_number || null,
          vintage_year: parseInt(newOffset.vintage_year),
          amount_tco2e: parseFloat(newOffset.amount_tco2e),
          remaining_tco2e: parseFloat(newOffset.amount_tco2e),
          purchase_date: purchaseDate,
          purchase_cost: newOffset.purchase_cost ? parseFloat(newOffset.purchase_cost) : null,
          notes: newOffset.notes || null
        })
        .select()
        .single();

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'carbon_offset',
        resource_id: data.id,
        action: 'create',
        description: `Added carbon offset: ${newOffset.project_name} (${newOffset.amount_tco2e} tCO₂-e)`
      });

      toast.success('Carbon offset added successfully!');
      setShowAddModal(false);
      setNewOffset({
        project_name: '',
        credit_provider: '',
        retirement_serial_number: '',
        vintage_year: new Date().getFullYear(),
        amount_tco2e: '',
        purchase_date: '',
        purchase_cost: '',
        notes: ''
      });
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error adding offset:', error);
      toast.error(getErrorMessage(error, 'Failed to add offset'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    if (!selectedOffset || !organisation) return;

    setSubmitting(true);
    try {
      const allocAmount = parseFloat(allocation.amount_tco2e);

      // Validate amount doesn't exceed remaining
      if (allocAmount > selectedOffset.remaining_tco2e) {
        toast.error(`Cannot allocate more than remaining amount (${selectedOffset.remaining_tco2e.toFixed(2)} tCO₂-e)`);
        setSubmitting(false);
        return;
      }

      const { data, error } = await supabase
        .from('offset_allocations')
        .insert({
          id: crypto.randomUUID(),
          organisation_id: organisation.id,
          carbon_offset_id: selectedOffset.id,
          financial_year: parseInt(allocation.financial_year),
          amount_tco2e: allocAmount,
          notes: allocation.notes || null
        })
        .select()
        .single();

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'offset_allocation',
        resource_id: data.id,
        action: 'create',
        description: `Allocated ${allocAmount} tCO₂-e to FY ${allocation.financial_year - 1}/${allocation.financial_year}`
      });

      toast.success('Offset allocated successfully!');
      setShowAllocateModal(false);
      setSelectedOffset(null);
      setAllocation({ financial_year: new Date().getFullYear() + 1, amount_tco2e: '', notes: '' });
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error allocating offset:', error);
      toast.error(getErrorMessage(error, 'Failed to allocate offset'));
    } finally {
      setSubmitting(false);
    }
  };

  // Edit allocation handler
  const handleEditAllocation = async (e) => {
    e.preventDefault();
    if (!selectedAllocation || !selectedOffset || !organisation) return;

    setSubmitting(true);
    try {
      const newAmount = parseFloat(editAllocation.amount_tco2e);
      const maxAllowable = selectedOffset.remaining_tco2e + selectedAllocation.amount_tco2e;

      if (newAmount > maxAllowable) {
        toast.error(`Cannot allocate more than ${maxAllowable.toFixed(2)} tCO₂-e`);
        setSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('offset_allocations')
        .update({
          financial_year: parseInt(editAllocation.financial_year),
          amount_tco2e: newAmount,
          notes: editAllocation.notes || null
        })
        .eq('id', selectedAllocation.id);

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'offset_allocation',
        resource_id: selectedAllocation.id,
        action: 'update',
        description: `Updated allocation to ${newAmount} tCO₂-e for FY ${editAllocation.financial_year - 1}/${editAllocation.financial_year}`
      });

      toast.success('Allocation updated successfully!');
      setShowEditAllocationModal(false);
      setSelectedAllocation(null);
      setSelectedOffset(null);
      setEditAllocation({ financial_year: '', amount_tco2e: '', notes: '' });
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error updating allocation:', error);
      toast.error(getErrorMessage(error, 'Failed to update allocation'));
    } finally {
      setSubmitting(false);
    }
  };

  // Delete allocation handler
  const handleDeleteAllocation = async (allocationId) => {
    if (!organisation) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('offset_allocations')
        .delete()
        .eq('id', allocationId);

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'offset_allocation',
        resource_id: allocationId,
        action: 'delete',
        description: 'Deleted offset allocation'
      });

      toast.success('Allocation deleted successfully!');
      setShowDeleteAllocationConfirm(null);
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error deleting allocation:', error);
      toast.error(getErrorMessage(error, 'Failed to delete allocation'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadCertificate = async (offsetId, file) => {
    if (!organisation) return;

    setUploadingCert(offsetId);
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${organisation.id}/${offsetId}/certificate_${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('certificates')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('certificates')
        .getPublicUrl(fileName);

      // Update offset with certificate URL
      const { error: updateError } = await supabase
        .from('carbon_offsets')
        .update({ certificate_url: urlData.publicUrl })
        .eq('id', offsetId);

      if (updateError) throw updateError;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'carbon_offset',
        resource_id: offsetId,
        action: 'update',
        description: 'Uploaded certificate'
      });

      toast.success('Certificate uploaded!');
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error('Failed to upload certificate');
    } finally {
      setUploadingCert(null);
    }
  };

  const handleDeleteCertificate = async (offsetId) => {
    if (!organisation) return;

    setSubmitting(true);
    try {
      // Update offset with certificate URL = null
      const { error: updateError } = await supabase
        .from('carbon_offsets')
        .update({ certificate_url: null })
        .eq('id', offsetId);

      if (updateError) throw updateError;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'carbon_offset',
        resource_id: offsetId,
        action: 'update',
        description: 'Deleted certificate'
      });

      toast.success('Certificate deleted!');
      setShowDeleteCertificateConfirm(null);
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOffset = async (offsetId) => {
    if (!organisation) return;

    setSubmitting(true);
    try {
      // First delete all allocations for this offset
      const { error: allocError } = await supabase
        .from('offset_allocations')
        .delete()
        .eq('carbon_offset_id', offsetId);

      if (allocError) throw allocError;

      // Then delete the offset
      const { error } = await supabase
        .from('carbon_offsets')
        .delete()
        .eq('id', offsetId);

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        organisation_id: organisation.id,
        resource_type: 'carbon_offset',
        resource_id: offsetId,
        action: 'delete',
        description: 'Deleted carbon offset and all allocations'
      });

      toast.success('Offset deleted');
      setShowDeleteOffsetConfirm(null);
      await fetchOffsetsData(organisation.id);
    } catch (error) {
      console.error('Error deleting offset:', error);
      toast.error('Failed to delete offset');
    } finally {
      setSubmitting(false);
    }
  };

  const getProviderLabel = (value) => {
    const provider = CREDIT_PROVIDERS.find(p => p.value === value);
    return provider ? provider.label : value;
  };

  if (!loading && !can(FEATURES.OFFSETS_VIEW)) {
    const reason = canWithReason(FEATURES.OFFSETS_VIEW);
    return (
      <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="offsets-page-locked">
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#74A57F] to-[#5F8575] p-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Carbon Offset Management</h1>
              <p className="text-white/80">Pro Feature</p>
            </div>

            <div className="p-8">
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <Leaf className="w-5 h-5 text-[#74A57F] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#003B5C]">Track Carbon Credits</p>
                    <p className="text-sm text-slate-500">Log ACCUs, Verra VCS, Gold Standard and other offset purchases</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-[#74A57F] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#003B5C]">Audit-Ready Ledger</p>
                    <p className="text-sm text-slate-500">Store retirement serial numbers and certificates</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <TrendingDown className="w-5 h-5 text-[#74A57F] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#003B5C]">Net Zero Progress</p>
                    <p className="text-sm text-slate-500">Allocate offsets to reporting years and track net emissions</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/billing')}
                className="w-full bg-[#003B5C] hover:bg-[#002A47] text-white"
              >
                Upgrade to Pro - $22/month
              </Button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#003B5C] animate-spin mb-4" />
          <p className="text-[#003B5C]">Loading offset data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="offsets-page">
      {/* Role-based access warning for restricted users */}
      {canWithReason(FEATURES.OFFSETS_ADD).isHidden && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center space-x-3">
            <Eye className="w-5 h-5 text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-medium">View Only Access</span> — You can view carbon offsets and allocations. Contact the owner for editing permissions.
            </p>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Title with Add Button */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Leaf className="w-7 h-7 mr-3 text-[#74A57F]" />
              Carbon Offset Management
            </h1>
            <p className="text-slate-600 mt-1">{organisation?.name} • Pro Feature</p>
          </div>
          {!canWithReason(FEATURES.OFFSETS_ADD).isHidden && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#74A57F] text-white hover:bg-[#5F8575]"
              data-testid="add-offset-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Carbon Credit
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Total Purchased</p>
              <p className="text-2xl font-bold text-[#003B5C]">{summary.total_purchased.toFixed(2)} <span className="text-sm font-normal">tCO₂-e</span></p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Total Retired</p>
              <p className="text-2xl font-bold text-[#74A57F]">{summary.total_retired.toFixed(2)} <span className="text-sm font-normal">tCO₂-e</span></p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Remaining Inventory</p>
              <p className="text-2xl font-bold text-amber-600">{summary.remaining_inventory.toFixed(2)} <span className="text-sm font-normal">tCO₂-e</span></p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Offset Entries</p>
              <p className="text-2xl font-bold text-[#003B5C]">{summary.offsets_count}</p>
            </div>
          </div>
        )}

        {/* Net Emissions by Year */}
        {summary && Object.keys(summary.by_year).length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-[#003B5C] mb-4">Net Emissions by Year</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-600">Financial Year</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-600">Gross Emissions</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-slate-600">Retired Offsets</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-[#003B5C]">Net Emissions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.by_year)
                    .sort(([a], [b]) => b - a)
                    .map(([year, data]) => (
                      <tr key={year} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-sm text-slate-700">FY {year - 1}/{year}</td>
                        <td className="py-3 px-4 text-sm text-right text-red-600">{data.gross_emissions.toFixed(2)} t</td>
                        <td className="py-3 px-4 text-sm text-right text-[#74A57F]">-{data.retired_offsets.toFixed(2)} t</td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-[#003B5C]">{data.net_emissions.toFixed(2)} t</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Offsets List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-[#003B5C]">Offset Inventory</h3>
          </div>

          {offsets.length === 0 ? (
            <div className="text-center py-16">
              <Leaf className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">No carbon offsets recorded yet</p>
              <p className="text-sm text-slate-400 mb-4">Add your first offset purchase to start tracking</p>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-[#74A57F] text-white hover:bg-[#5F8575]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Offset
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {offsets.map((offset) => (
                <div key={offset.id} className="p-6 hover:bg-slate-50 transition-colors" data-testid={`offset-${offset.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#74A57F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Leaf className="w-6 h-6 text-[#74A57F]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#003B5C]">{offset.project_name}</h4>
                        <p className="text-sm text-slate-500">{getProviderLabel(offset.credit_provider)}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                          <span>Vintage: {offset.vintage_year}</span>
                          {offset.retirement_serial_number && (
                            <span>Serial: {offset.retirement_serial_number}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-bold text-[#003B5C]">{offset.amount_tco2e.toFixed(2)} <span className="text-sm font-normal">tCO₂-e</span></p>
                      <p className="text-sm text-amber-600">
                        Remaining: {offset.remaining_tco2e.toFixed(2)} t
                      </p>
                    </div>
                  </div>

                  {/* Allocations */}
                  {offset.allocations && offset.allocations.length > 0 && (
                    <div className="mt-4 bg-slate-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-slate-600 mb-2">Allocations</p>
                      <div className="space-y-2">
                        {offset.allocations.map((alloc) => (
                          <div
                            key={alloc.id}
                            className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200"
                          >
                            <div className="flex items-center space-x-3">
                              <span className="inline-flex items-center px-2 py-1 bg-[#74A57F]/20 text-[#74A57F] rounded text-xs font-medium">
                                FY {alloc.financial_year - 1}/{alloc.financial_year}
                              </span>
                              <span className="text-sm text-slate-700 font-medium">
                                {alloc.amount_tco2e.toFixed(2)} tCO₂-e
                              </span>
                              {alloc.notes && (
                                <span className="text-xs text-slate-500 truncate max-w-[150px]" title={alloc.notes}>
                                  {alloc.notes}
                                </span>
                              )}
                            </div>
                            {!canWithReason(FEATURES.OFFSETS_ALLOCATE).isHidden && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedAllocation(alloc);
                                    setSelectedOffset(offset);
                                    setEditAllocation({
                                      financial_year: alloc.financial_year.toString(),
                                      amount_tco2e: alloc.amount_tco2e.toString(),
                                      notes: alloc.notes || ''
                                    });
                                    setShowEditAllocationModal(true);
                                  }}
                                  className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit Allocation"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setShowDeleteAllocationConfirm(alloc)}
                                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Allocation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex items-center space-x-3">
                    {!canWithReason(FEATURES.OFFSETS_ALLOCATE).isHidden && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedOffset(offset);
                          setAllocation({
                            ...allocation,
                            amount_tco2e: offset.remaining_tco2e.toString()
                          });
                          setShowAllocateModal(true);
                        }}
                        disabled={offset.remaining_tco2e <= 0}
                        className="text-[#74A57F] border-[#74A57F]"
                      >
                        Allocate to Year
                      </Button>
                    )}

                    {offset.certificate_url ? (
                      <div className="flex items-center space-x-3">
                        <a
                          href={offset.certificate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#003B5C] hover:underline flex items-center"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View Certificate
                        </a>
                        {!canWithReason(FEATURES.OFFSETS_CERTIFICATE_MANAGE).isHidden && (
                          <button
                            onClick={() => setShowDeleteCertificateConfirm(offset)}
                            className="text-sm text-slate-400 hover:text-red-600 flex items-center"
                            title="Delete Certificate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      !canWithReason(FEATURES.OFFSETS_CERTIFICATE_MANAGE).isHidden && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleUploadCertificate(offset.id, e.target.files[0]);
                              }
                            }}
                          />
                          <span className="text-sm text-slate-500 hover:text-[#003B5C] flex items-center">
                            {uploadingCert === offset.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Upload Certificate
                          </span>
                        </label>
                      )
                    )}

                    {!canWithReason(FEATURES.OFFSETS_DELETE).isHidden && (
                      <button
                        onClick={() => setShowDeleteOffsetConfirm(offset)}
                        className="text-sm text-red-500 hover:text-red-700 flex items-center ml-auto px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete Offset
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Carbon Credit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-[#003B5C]">Add Carbon Offset</h3>
              <p className="text-sm text-slate-500">Record a new offset purchase</p>
            </div>

            <form onSubmit={handleAddOffset} className="p-6 space-y-4">
              <div>
                <Label className="text-[#003B5C]">Project Name *</Label>
                <Input
                  value={newOffset.project_name}
                  onChange={(e) => setNewOffset({ ...newOffset, project_name: e.target.value })}
                  placeholder="e.g., Reforestation Project A"
                  required
                />
              </div>

              <div>
                <Label className="text-[#003B5C]">Credit Provider *</Label>
                <Select
                  value={newOffset.credit_provider}
                  onValueChange={(val) => setNewOffset({ ...newOffset, credit_provider: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_PROVIDERS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#003B5C]">Amount (tCO₂-e) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newOffset.amount_tco2e}
                    onChange={(e) => setNewOffset({ ...newOffset, amount_tco2e: e.target.value })}
                    placeholder="e.g., 100"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#003B5C]">Vintage Year *</Label>
                  <Input
                    type="number"
                    value={newOffset.vintage_year}
                    onChange={(e) => setNewOffset({ ...newOffset, vintage_year: e.target.value })}
                    placeholder="2024"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#003B5C]">Retirement Serial Number</Label>
                <Input
                  value={newOffset.retirement_serial_number}
                  onChange={(e) => setNewOffset({ ...newOffset, retirement_serial_number: e.target.value })}
                  placeholder="Required for audit compliance"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#003B5C]">Purchase Date</Label>
                  <Input
                    type="date"
                    value={newOffset.purchase_date}
                    onChange={(e) => setNewOffset({ ...newOffset, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-[#003B5C]">Purchase Cost (AUD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newOffset.purchase_cost}
                    onChange={(e) => setNewOffset({ ...newOffset, purchase_cost: e.target.value })}
                    placeholder="e.g., 2500"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#003B5C]">Notes</Label>
                <Input
                  value={newOffset.notes}
                  onChange={(e) => setNewOffset({ ...newOffset, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#74A57F] text-white hover:bg-[#5F8575]"
                  disabled={submitting || !newOffset.project_name || !newOffset.credit_provider || !newOffset.amount_tco2e}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Carbon Credit'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Allocate Modal */}
      {showAllocateModal && selectedOffset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md"
          >
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-[#003B5C]">Allocate Offset Credits</h3>
              <p className="text-sm text-slate-500">{selectedOffset.project_name}</p>
              <p className="text-sm text-amber-600 mt-1">Available: {selectedOffset.remaining_tco2e.toFixed(2)} tCO₂-e</p>
            </div>

            <form onSubmit={handleAllocate} className="p-6 space-y-4">
              <div>
                <Label className="text-[#003B5C]">Financial Year *</Label>
                <Select
                  value={allocation.financial_year.toString()}
                  onValueChange={(val) => setAllocation({ ...allocation, financial_year: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <SelectItem key={y} value={y.toString()}>FY {y - 1}/{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#003B5C]">Amount to Retire (tCO₂-e) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  max={selectedOffset.remaining_tco2e}
                  value={allocation.amount_tco2e}
                  onChange={(e) => setAllocation({ ...allocation, amount_tco2e: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label className="text-[#003B5C]">Notes</Label>
                <Input
                  value={allocation.notes}
                  onChange={(e) => setAllocation({ ...allocation, notes: e.target.value })}
                  placeholder="Optional allocation notes"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAllocateModal(false);
                    setSelectedOffset(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#74A57F] text-white hover:bg-[#5F8575]"
                  disabled={submitting || !allocation.amount_tco2e || parseFloat(allocation.amount_tco2e) > selectedOffset.remaining_tco2e}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Allocate'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Allocation Modal */}
      {showEditAllocationModal && selectedAllocation && selectedOffset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl"
          >
            <h3 className="text-xl font-semibold text-[#003B5C] mb-4">Edit Allocation</h3>
            <p className="text-sm text-slate-600 mb-4">
              Editing allocation for: <strong>{selectedOffset.project_name}</strong>
            </p>
            <form onSubmit={handleEditAllocation} className="space-y-4">
              <div>
                <Label htmlFor="editFy">Financial Year</Label>
                <Select
                  value={editAllocation.financial_year.toString()}
                  onValueChange={(val) => setEditAllocation({ ...editAllocation, financial_year: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + 2 - i;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          FY {year - 1}/{year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="editAmount">Amount (tCO₂-e)</Label>
                <Input
                  id="editAmount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editAllocation.amount_tco2e}
                  onChange={(e) => setEditAllocation({ ...editAllocation, amount_tco2e: e.target.value })}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Original: {selectedAllocation.amount_tco2e.toFixed(2)} t |
                  Available in offset: {(selectedOffset.remaining_tco2e + selectedAllocation.amount_tco2e).toFixed(2)} t
                </p>
              </div>
              <div>
                <Label htmlFor="editNotes">Notes</Label>
                <Input
                  id="editNotes"
                  value={editAllocation.notes}
                  onChange={(e) => setEditAllocation({ ...editAllocation, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditAllocationModal(false);
                    setSelectedAllocation(null);
                    setSelectedOffset(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#003B5C] text-white hover:bg-[#002A42]"
                  disabled={submitting || !editAllocation.amount_tco2e || parseFloat(editAllocation.amount_tco2e) > (selectedOffset.remaining_tco2e + selectedAllocation.amount_tco2e)}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Allocation Confirmation */}
      {showDeleteAllocationConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#003B5C]">Delete Allocation</h3>
            </div>
            <p className="text-slate-600 mb-4">
              Are you sure you want to delete this allocation?
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm">
                <span className="text-slate-500">Financial Year:</span>{' '}
                <strong>FY {showDeleteAllocationConfirm.financial_year - 1}/{showDeleteAllocationConfirm.financial_year}</strong>
              </p>
              <p className="text-sm">
                <span className="text-slate-500">Amount:</span>{' '}
                <strong>{showDeleteAllocationConfirm.amount_tco2e.toFixed(2)} tCO₂-e</strong>
              </p>
              {showDeleteAllocationConfirm.notes && (
                <p className="text-sm">
                  <span className="text-slate-500">Notes:</span>{' '}
                  {showDeleteAllocationConfirm.notes}
                </p>
              )}
            </div>
            <p className="text-sm text-amber-600 mb-6">
              The allocated amount will be restored to the carbon offset's available balance.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteAllocationConfirm(null)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteAllocation(showDeleteAllocationConfirm.id)}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Allocation'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Delete Offset Confirmation */}
      {showDeleteOffsetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#003B5C]">Delete Carbon Offset</h3>
            </div>
            <p className="text-slate-600 mb-4">
              Are you sure you want to delete this offset? This action cannot be undone.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm">
                <span className="text-slate-500">Project:</span>{' '}
                <strong>{showDeleteOffsetConfirm.project_name}</strong>
              </p>
              <p className="text-sm mt-1">
                <span className="text-slate-500">Amount:</span>{' '}
                <strong>{showDeleteOffsetConfirm.amount_tco2e.toFixed(2)} tCO₂-e</strong>
              </p>
            </div>
            <p className="text-sm text-amber-600 mb-6 font-medium text-center">
              Warning: All allocations of this offset to financial years will also be instantly deleted.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteOffsetConfirm(null)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteOffset(showDeleteOffsetConfirm.id)}
                className="bg-red-600 text-white hover:bg-red-700 flex-1"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Delete Offset'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Certificate Confirmation */}
      {showDeleteCertificateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#003B5C]">Delete Certificate</h3>
            </div>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete the certificate for <strong>{showDeleteCertificateConfirm.project_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteCertificateConfirm(null)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteCertificate(showDeleteCertificateConfirm.id)}
                className="bg-red-600 text-white hover:bg-red-700 flex-1"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Delete Certificate'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default OffsetsPage;
